import { cx } from "@linaria/core";
import type { RefObject } from "react";
import { memo, useCallback, useEffect, useRef } from "react";

import { getTransformation, useInfiniteScrolling } from "./hook.infinite";
import { useRegisterKeyboardEvents } from "./hook.keyboard";
import type { Pagination } from "./hook.pagination";
import { SliderItem } from "./item";
import * as styles from "./slider.styles";
import { throttleToAnimationFrame } from "../../utils/dom.throttle-to-animation-frame";
import { SwipeDirections, useSwipe } from "../../hooks/swipe";

export type SliderProps<T extends Record<string, unknown>> = {
  pagination: Pagination;
  items: T[];
  wrapperRef: RefObject<HTMLDivElement | null>;
  infinite: boolean;

  /**
   * specifies which item to focus when tab initially reaches the slider.
   *
   * @default "left"
   */
  focusTarget?: "left" | "center" | "right";

  className?: string;
  children(
    item: T,
    extra: {
      index: number;
      isVisible: boolean;
      location: "left" | "right" | "center";
      disabled: boolean;
    },
  ): React.JSX.Element;
};

/** Wrapper on top of memo to ensure that generic type can be propagated */
const genericMemo: <T>(component: T) => T = memo;

/**
 * generic slider that can wraps an arbitrary amount of children in a carousel,
 * fully equipped with keyboard, touch and mouse-events.
 */
export const Slider = genericMemo(function Slider<
  T extends Record<string, unknown>,
>(props: SliderProps<T>): React.JSX.Element {
  const sliderRef = useRef<HTMLUListElement>(null);

  const {
    disabled,
    spacingBefore,
    items: inifiniteItems,
  } = useInfiniteScrolling(sliderRef, props.pagination, props.items);
  useRoundedSliderWidth(props.wrapperRef, sliderRef);

  const onSwipe = useCallback(
    (direction: SwipeDirections) => {
      switch (direction) {
        case "right-to-left":
          props.pagination.goForwards();
          break;

        case "left-to-right":
          props.pagination.goBackwards();
          break;
      }
    },
    [props.pagination],
  );

  useSwipe(props.wrapperRef, onSwipe, { touchOnly: true });

  const hasFocusWithin = useRegisterKeyboardEvents(
    props.pagination,
    sliderRef,
    { target: props.focusTarget ?? "left" },
  );

  const items = props.infinite
    ? inifiniteItems
    : props.items.map((it, index) => ({
        item: {
          ...it,
          index,
          key: String(index),
        },
        disabled: false,
      }));

  return (
    <div ref={props.wrapperRef} className={cx(props.className, styles.wrapper)}>
      <ul
        tabIndex={hasFocusWithin ? undefined : 0}
        ref={sliderRef}
        className={styles.slider}
        style={{
          transform:
            !props.infinite || !disabled
              ? getTransformation(props.pagination, props.infinite)
              : undefined,
        }}
      >
        {props.infinite && !disabled && (
          <div
            className={styles.spacer}
            style={{
              width: `calc(${spacingBefore} * (100% + ${styles.sliderTokens.itemMargin()}))`,
            }}
          />
        )}

        {items.map((it) => (
          <SliderItem
            key={it.item.key}
            disabled={it.disabled}
            item={it.item}
            sliderRef={sliderRef}
            infinite={props.infinite}
            pagination={props.pagination}
          >
            {props.children}
          </SliderItem>
        ))}
      </ul>
    </div>
  );
});

/**
 * Annoyingly having a slider with a non-rounded decimal as its width may
 * quickly result in slight "jumps" when adjust the spacer sizing after
 * finishing a transition.
 *
 * We cannot change the default behavior of browsers and as such we round the
 * width of the slider to the nearest integer that makes sense. This avoids
 * jumps related to rounding errors.
 */
function useRoundedSliderWidth(
  wrapperRef: RefObject<HTMLDivElement | null>,
  sliderRef: RefObject<HTMLUListElement | null>,
) {
  useEffect(() => {
    const wrapper = wrapperRef.current;
    const slider = sliderRef.current;

    if (!wrapper || !slider) {
      return;
    }

    const onResize = () => {
      slider.style.width = `calc(${
        wrapper.clientWidth
      }px - 2 * ${styles.sliderTokens.containerPadding()}`;
    };

    onResize();
    const observer = new ResizeObserver(throttleToAnimationFrame(onResize));
    observer.observe(wrapper);

    return () => {
      observer.disconnect();
    };
  }, [sliderRef, wrapperRef]);
}
